import { useState } from 'react';
import { Stack, CaretUp, CaretDown, MagnifyingGlass, X } from '@phosphor-icons/react';
import LayerList from './LayerList';
import { useSceneStore } from '../../stores/sceneStore';
import { useDragDrop } from '../../hooks/useDragDrop';
import type { Asset } from '@spine-workspace/shared';

interface SceneLayersProps {
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  viewOnly?: boolean;
}

export default function SceneLayers({ isCollapsed, onToggleCollapse, viewOnly }: SceneLayersProps) {
  const { layers } = useSceneStore();
  const { addAssetsAtCenter } = useDragDrop();
  const [layerFilter, setLayerFilter] = useState('');

  function onDragOver(e: React.DragEvent) {
    if (e.dataTransfer.types.includes('application/spine-asset')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    const data = e.dataTransfer.getData('application/spine-asset');
    if (!data) return;
    try {
      const parsed = JSON.parse(data) as Asset | Asset[];
      const assets = Array.isArray(parsed) ? parsed : [parsed];
      addAssetsAtCenter(assets);
    } catch { /* ignore */ }
  }

  return (
    <div className="flex flex-col h-full bg-bg-surface" onDragOver={viewOnly ? undefined : onDragOver} onDrop={viewOnly ? undefined : onDrop}>
      {/* Section header */}
      <div className="px-4 py-3 border-b border-border-subtle flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Stack size={14} className="text-accent" />
          <span className="font-serif text-base font-medium text-accent tracking-[0.01em]">Hierarchy</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-text-tertiary bg-bg-elevated px-2 py-0.5 rounded-sm">
            {layers.length}
          </span>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="w-5 h-5 flex items-center justify-center rounded text-text-tertiary hover:text-accent hover:bg-bg-hover transition-all duration-fast"
              title={isCollapsed ? 'Expand Hierarchy' : 'Collapse Hierarchy'}
              data-testid="hierarchy-collapse-btn"
            >
              {isCollapsed
                ? <CaretDown size={10} weight="bold" />
                : <CaretUp size={10} weight="bold" />
              }
            </button>
          )}
        </div>
      </div>

      {/* Search filter — only when there are layers to filter */}
      {layers.length > 2 && (
        <div className="px-3 py-1.5 border-b border-border-subtle shrink-0">
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" />
            <input
              value={layerFilter}
              onChange={(e) => setLayerFilter(e.target.value)}
              placeholder="Filter layers…"
              data-testid="layer-search-input"
              className="w-full bg-bg-input border border-border text-text-primary rounded-md pl-7 pr-7 py-[5px] text-xs font-sans placeholder:text-text-tertiary hover:bg-bg-hover focus:outline-none focus:border-accent transition-all duration-normal"
            />
            {layerFilter && (
              <button
                onClick={() => setLayerFilter('')}
                data-testid="layer-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary"
              >
                <X size={11} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Layer list — fills remaining height, clipped by parent when collapsed */}
      <div className="flex-1 overflow-y-auto">
        <LayerList filter={layerFilter} />
      </div>
    </div>
  );
}
